import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../ogComponent/spinner";
import Modal from "../ogComponent/modal";
import axios from "axios";
import { Toast } from 'primereact/toast';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Pagination } from "@mui/material";
import dayjs from "dayjs";
import "./selfLeav.css";

const SelfLeaveTable = () => {
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balanceLeave, setBalanceLeave] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [submitting, setSubmitting] = useState(false);
  const rowsPerPage = 10;

  const [modalValues, setModalValues] = useState({
    leave_duration: null,
    leave_type: null,
    start_date: '',
    end_date: '',
    reason: '',
    days_difference: 0
  });

  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const toast = useRef(null);
  const token = localStorage.getItem("accessToken");

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = leaveData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(leaveData.length / rowsPerPage);

  const hideBackdrop = () => {
    const backdrop = document.querySelector('.modal-backdrop');
    if (backdrop) {
      document.body.removeChild(backdrop);
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const fetchBalanceLeave = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/api/leavemanagement/balance-leave/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBalanceLeave(response.data.balance_leave);
    } catch (error) {
      console.error('Error fetching balance leave:', error);
    }
  };

  const fetchLeaveData = async () => {
    setLoading(true);
    try {
      let apiUrl = `${process.env.REACT_APP_API}/api/leavemanagement/leaves/employee/${userId}/`;
      if (selectedMonth) {
        const formattedMonth = dayjs(selectedMonth).format("MM-YYYY");
        apiUrl += `?date=${formattedMonth}`;
      }

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setLeaveData(response.data.leaves || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leave data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId && token) {
      fetchBalanceLeave();
      fetchLeaveData();
    }
  }, [userId, token, selectedMonth]);

  const calculateDaysDifference = (startDate, endDate, leaveDuration) => {
    if (!startDate || !endDate || !leaveDuration) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const baseDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    const durationMap = {
      "Quarter Day": 0.25,
      "Half Day": 0.5,
      "Three-quarters day": 0.75,
      "Full Day": 1
    };

    const multiplier = durationMap[leaveDuration?.value] || 1;
    return baseDays * multiplier;
  };

  const handleRowClick = (id) => {
    navigate(`/user/leaveDetails/${userId}/${id}/`);
  };

  const handleModalFieldChange = (name, value) => {
    setModalValues(prev => {
      const newValues = { ...prev, [name]: value };

      if (name === 'start_date' || name === 'end_date' || name === 'leave_duration') {
        const daysDiff = calculateDaysDifference(
          newValues.start_date,
          newValues.end_date,
          newValues.leave_duration
        );
        newValues.days_difference = daysDiff;

        if (daysDiff > balanceLeave && name === 'end_date') {
          toast.current.show({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Leave days exceed balance leave!',
            life: 3000,
          });
        }
      }

      return newValues;
    });
  };

  const handleSubmit = async (data) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const daysDiff = calculateDaysDifference(
        data.start_date,
        data.end_date,
        data.leave_duration
      );

      if (daysDiff > balanceLeave && data.leave_type?.value === "Casual Leave") {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Casual Leave not available: Exceeds balance leave!',
          life: 3000,
        });
        setSubmitting(false);
        return;
      }

      const leaveData = {
        leave_duration: data.leave_duration?.value,
        leave_type: data.leave_type?.value,
        end_date: data.end_date,
        reason: data.reason,
        start_date: data.start_date,
        total_leave_days: daysDiff.toString(),
        emp_id: userId,
      };

      await axios.post(
        `${process.env.REACT_APP_API}/api/leavemanagement/leaves/`,
        leaveData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Leave Applied Successfully!',
        life: 3000,
      });

      setModalValues({
        leave_duration: null,
        leave_type: null,
        start_date: '',
        end_date: '',
        reason: '',
        days_difference: 0
      });

      await fetchLeaveData();
      await fetchBalanceLeave();
      hideBackdrop();

    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || "Error submitting, please try again!",
        life: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const modalFieldsConfig = [
    {
      name: "balance_leave",
      label: "Balance Leave",
      type: "number",
      disabled: true,
      readOnly: true,
      value: balanceLeave,
    },
    {
      name: "leave_duration",
      label: "Leave Duration",
      type: "select",
      options: [
        { value: "Quarter Day", label: "Quarter Day" },
        { value: "Half Day", label: "Half Day" },
        { value: "Three-quarters day", label: "3 Quarter Day" },
        { value: "Full Day", label: "Full Day" },
      ],
      required: true,
      onChange: (value) => handleModalFieldChange("leave_duration", value),
      value: modalValues.leave_duration
    },
    {
      name: "leave_type",
      label: "Leave Type",
      type: "select",
      options: [
        { value: "Work From Home", label: "Work From Home" },
        { value: "Casual Leave", label: "Casual Leave" },
        { value: "Loss of Pay", label: "Loss of Pay" },
      ],
      required: true,
      onChange: (value) => handleModalFieldChange("leave_type", value),
      value: modalValues.leave_type
    },
    {
      name: "start_date",
      label: "Start Date",
      type: "date",
      required: true,
      onChange: (value) => handleModalFieldChange("start_date", value),
      value: modalValues.start_date
    },
    {
      name: "end_date",
      label: "End Date",
      type: "date",
      required: true,
      onChange: (value) => handleModalFieldChange("end_date", value),
      value: modalValues.end_date
    },
    {
      name: "reason",
      label: "Leave Reason",
      type: "textarea",
      required: true,
      onChange: (value) => handleModalFieldChange("reason", value),
      value: modalValues.reason
    },
  ];

  if (loading) return <Spinner />;

  return (
    <div className="selfLeaveContainer w-100 d-flex flex-column p-0 gap-4">
      <Toast ref={toast} position="bottom-right" />

      <div className="row g-4 mb-2">
        <div className="col-12 col-md-4">
          <div className="leave-balance-card d-flex flex-column justify-content-center">
            <span style={{ fontSize: '14px', fontWeight: 500, opacity: 0.9 }}>Available Balance</span>
            <div className="d-flex align-items-baseline gap-2 mt-2">
              <h1 className="m-0" style={{ fontSize: '48px', fontWeight: 700 }}>{balanceLeave}</h1>
              <span style={{ fontSize: '16px', fontWeight: 600 }}>Days</span>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-8 d-flex align-items-center justify-content-md-end gap-3 flex-wrap">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={['month', 'year']}
              label="Filter by Month"
              value={selectedMonth}
              onChange={setSelectedMonth}
              format="MMMM YYYY"
              slotProps={{ textField: { size: "medium" } }}
              sx={{
                background: 'white',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
              }}
            />
          </LocalizationProvider>
          <button className="apply-btn-premium" data-bs-toggle="modal" data-bs-target="#staticBackdrop">
            <i className="bi bi-plus-lg me-2"></i>Apply New Leave
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2 px-2">
        <h3 className="m-0">My Leave History</h3>
      </div>

      {leaveData.length === 0 ? (
        <div className="d-flex flex-column justify-content-center align-items-center py-5 modern-table-card">
          <img src="/assets/file.png" alt="empty" style={{ width: 120, opacity: 0.2 }} />
          <p className="mt-3 fw-medium text-muted">No leave records found for this period</p>
          <button className="btn btn-link text-primary text-decoration-none p-0" onClick={() => setSelectedMonth(null)}>View all records</button>
        </div>
      ) : (
        <div className="modern-table-card">
          <div className="table-responsive">
            <table className="self-leave-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Dates</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th className="text-center">Days</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((leave, index) => (
                  <tr key={leave.id} onClick={() => handleRowClick(leave.id)}>
                    <td data-label="#">{indexOfFirstRow + index + 1}</td>
                    <td data-label="Dates">
                      <div className="date-display">
                        <span className="date-main">{dayjs(leave.start_date).format("DD MMM YYYY")}</span>
                        <span className="date-sub">to {dayjs(leave.end_date).format("DD MMM YYYY")}</span>
                      </div>
                    </td>
                    <td data-label="Type">
                      <span className="fw-semibold text-main">{leave.leave_type || "N/A"}</span>
                    </td>
                    <td data-label="Duration">
                      <span className="text-muted">{leave.leave_duration || "Full Day"}</span>
                    </td>
                    <td data-label="Days" className="text-center fw-bold text-primary">
                      {leave.total_leave_days || 0}
                    </td>
                    <td data-label="Status" className="text-center">
                      <div className={`status-badge ${leave.status === "Approved" ? "status-approved" :
                          leave.status === "Pending" ? "status-pending" : "status-rejected"
                        }`}>
                        <span className="dot-indicator"></span>
                        {leave.status || "Pending"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontWeight: 600,
                    borderRadius: '10px',
                  },
                }}
              />
            </div>
          )}
        </div>
      )}

      <Modal
        id="staticBackdrop"
        heading="Apply New Leave"
        fields={modalFieldsConfig}
        onSubmit={handleSubmit}
        submitText={submitting ? "Applying..." : "Confirm Application"}
        submitDisabled={submitting}
      />
    </div>
  );
};

export default SelfLeaveTable;
